import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";

/**
 * A point on a cumulative-P/L curve. `t` is an epoch-millis timestamp, `v` is
 * the cumulative P/L (USD) at that time.
 */
export interface ChartPoint {
  t: number;
  v: number;
}

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  points: ChartPoint[];
}

const M = { top: 16, right: 22, bottom: 28, left: 66 };

/**
 * Dependency-free SVG equity-curve chart. Renders one polyline per series on a
 * shared time axis, with nice y-ticks, a dashed zero baseline, an optional area
 * fill (single series), and a hover guide with a multi-series tooltip.
 *
 * Width is measured from the container (ResizeObserver) so the SVG renders at
 * 1 unit = 1px — keeping text crisp and mouse coordinates exact.
 */
export default function PnlChart({
  series,
  height = 340,
}: {
  series: ChartSeries[];
  height?: number;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(760);
  const [hoverX, setHoverX] = useState<number | null>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(Math.max(320, Math.floor(w)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const plotW = Math.max(1, width - M.left - M.right);
  const plotH = Math.max(1, height - M.top - M.bottom);

  const domain = useMemo(() => {
    const xs: number[] = [];
    const ys: number[] = [0];
    for (const s of series) {
      for (const p of s.points) {
        xs.push(p.t);
        ys.push(p.v);
      }
    }
    if (xs.length === 0) return null;
    let xMin = Math.min(...xs);
    let xMax = Math.max(...xs);
    if (xMin === xMax) {
      xMin -= 86400000;
      xMax += 86400000;
    }
    let yMin = Math.min(...ys);
    let yMax = Math.max(...ys);
    if (yMin === yMax) {
      yMin -= 1;
      yMax += 1;
    }
    const pad = (yMax - yMin) * 0.08;
    return { xMin, xMax, yMin: yMin - pad, yMax: yMax + pad };
  }, [series]);

  if (!domain) {
    return <div className="chart-empty muted">No trades to chart yet.</div>;
  }

  const x = (t: number) =>
    M.left + ((t - domain.xMin) / (domain.xMax - domain.xMin)) * plotW;
  const y = (v: number) =>
    M.top + (1 - (v - domain.yMin) / (domain.yMax - domain.yMin)) * plotH;

  const yTicks = niceTicks(domain.yMin, domain.yMax, 5).filter(
    (v) => v >= domain.yMin && v <= domain.yMax,
  );
  const xTickCount = Math.max(2, Math.min(7, Math.floor(plotW / 120)));
  const xTicks = timeTicks(domain.xMin, domain.xMax, xTickCount);
  const spanDays = (domain.xMax - domain.xMin) / 86400000;

  const hoverT =
    hoverX == null
      ? null
      : domain.xMin + ((hoverX - M.left) / plotW) * (domain.xMax - domain.xMin);
  const showHover = hoverX != null && hoverX >= M.left && hoverX <= M.left + plotW;

  function onMove(e: ReactMouseEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverX(e.clientX - rect.left);
  }

  const singleArea = series.length === 1;
  const baseY = M.top + plotH;

  return (
    <div className="chart-wrap" ref={wrapRef}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label="Cumulative profit and loss over time"
        onMouseMove={onMove}
        onMouseLeave={() => setHoverX(null)}
      >
        {yTicks.map((v) => (
          <g key={`y${v}`}>
            <line
              className={Math.abs(v) < 1e-9 ? "chart-zero" : "chart-grid"}
              x1={M.left}
              x2={M.left + plotW}
              y1={y(v)}
              y2={y(v)}
            />
            <text
              className="chart-axis"
              x={M.left - 8}
              y={y(v)}
              textAnchor="end"
              dominantBaseline="middle"
            >
              {usdShort(v)}
            </text>
          </g>
        ))}

        {xTicks.map((t, i) => (
          <text
            key={`x${i}`}
            className="chart-axis"
            x={x(t)}
            y={height - 8}
            textAnchor="middle"
          >
            {fmtDate(t, spanDays)}
          </text>
        ))}

        {singleArea && series[0].points.length > 1 && (
          <path
            className="chart-area"
            d={areaPath(series[0].points, x, y, baseY)}
            fill={series[0].color}
          />
        )}

        {series.map((s) => (
          <path
            key={s.key}
            className="chart-line"
            d={linePath(s.points, x, y)}
            stroke={s.color}
          />
        ))}

        {series.map((s) => {
          const last = s.points[s.points.length - 1];
          if (!last) return null;
          return (
            <circle
              key={`d${s.key}`}
              cx={x(last.t)}
              cy={y(last.v)}
              r={3}
              fill={s.color}
            />
          );
        })}

        {showHover && hoverT != null && (
          <>
            <line
              className="chart-hover-line"
              x1={hoverX}
              x2={hoverX}
              y1={M.top}
              y2={baseY}
            />
            {series.map((s) => {
              const v = interp(s.points, hoverT);
              if (v == null) return null;
              return (
                <circle
                  key={`h${s.key}`}
                  cx={hoverX}
                  cy={y(v)}
                  r={3.5}
                  fill={s.color}
                  stroke="var(--card)"
                  strokeWidth={1.5}
                />
              );
            })}
          </>
        )}
      </svg>

      {showHover && hoverT != null && (
        <div
          className="chart-tooltip"
          style={{ left: Math.min(hoverX + 14, width - 190), top: M.top }}
        >
          <div className="chart-tooltip-date">{fmtFullDate(hoverT)}</div>
          {series.map((s) => {
            const v = interp(s.points, hoverT);
            if (v == null) return null;
            return (
              <div key={`t${s.key}`} className="chart-tooltip-row">
                <span className="chart-swatch" style={{ background: s.color }} />
                <span className="chart-tooltip-label">{s.label}</span>
                <span
                  className={"chart-tooltip-val " + (v >= 0 ? "pnl-pos" : "pnl-neg")}
                >
                  {usdSigned(v)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Path builders
// ---------------------------------------------------------------------------

function linePath(
  points: ChartPoint[],
  x: (t: number) => number,
  y: (v: number) => number,
): string {
  if (points.length === 0) return "";
  return points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${x(p.t).toFixed(2)} ${y(p.v).toFixed(2)}`)
    .join(" ");
}

function areaPath(
  points: ChartPoint[],
  x: (t: number) => number,
  y: (v: number) => number,
  baseY: number,
): string {
  if (points.length === 0) return "";
  const top = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${x(p.t).toFixed(2)} ${y(p.v).toFixed(2)}`)
    .join(" ");
  const lastX = x(points[points.length - 1].t).toFixed(2);
  const firstX = x(points[0].t).toFixed(2);
  return `${top} L ${lastX} ${baseY.toFixed(2)} L ${firstX} ${baseY.toFixed(2)} Z`;
}

/** Linear interpolation of v at time t along a series (carries the endpoints). */
function interp(points: ChartPoint[], t: number): number | null {
  if (points.length === 0) return null;
  if (t <= points[0].t) return points[0].v;
  const last = points[points.length - 1];
  if (t >= last.t) return last.v;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (t <= b.t) {
      const span = b.t - a.t;
      if (span === 0) return b.v;
      return a.v + ((t - a.t) / span) * (b.v - a.v);
    }
  }
  return last.v;
}

// ---------------------------------------------------------------------------
// Tick + format helpers
// ---------------------------------------------------------------------------

function niceTicks(min: number, max: number, count: number): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) return [min];
  const range = niceNum(max - min, false);
  const step = niceNum(range / Math.max(1, count - 1), true);
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + step * 0.5; v += step) {
    ticks.push(Math.round(v / step) * step);
  }
  return ticks;
}

function niceNum(range: number, round: boolean): number {
  const exp = Math.floor(Math.log10(range || 1));
  const frac = range / Math.pow(10, exp);
  let nice: number;
  if (round) {
    nice = frac < 1.5 ? 1 : frac < 3 ? 2 : frac < 7 ? 5 : 10;
  } else {
    nice = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  }
  return nice * Math.pow(10, exp);
}

function timeTicks(min: number, max: number, count: number): number[] {
  const ticks: number[] = [];
  const step = (max - min) / Math.max(1, count - 1);
  for (let i = 0; i < count; i++) ticks.push(min + step * i);
  return ticks;
}

function usdShort(v: number): string {
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${sign}$${(a / 1_000_000).toFixed(a >= 10_000_000 ? 0 : 1)}M`;
  if (a >= 1_000) return `${sign}$${(a / 1_000).toFixed(a >= 10_000 ? 0 : 1)}k`;
  return `${sign}$${a.toFixed(0)}`;
}

function usdSigned(v: number): string {
  const sign = v > 0 ? "+" : "";
  return (
    sign +
    v.toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function fmtDate(t: number, spanDays: number): string {
  const d = new Date(t);
  if (spanDays > 365) {
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  }
  if (spanDays > 2) {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtFullDate(t: number): string {
  return new Date(t).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
